// in AiCompletedStatsDashboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import TradesDayHourChart from './aiStatsChart';
import CriteriaExplorer from './CriteriaExplorer';
import CriteriaFinder from "./CriteriaFinder";
import './AiTrades.css';

export default function AiCompletedStatsDashboard() {
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [investment, setInvestment] = useState('');
  const [leverage, setLeverage] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [advancedPredicate, setAdvancedPredicate] = useState(null);
  const [groupByDate, setGroupByDate] = useState(false);

  // persist show/hide chart
  const [showChart, setShowChart] = useState(() => {
    const saved = localStorage.getItem('showDayHourChart');
    return saved ? saved === '1' : true;
  });
  useEffect(() => {
    localStorage.setItem('showDayHourChart', showChart ? '1' : '0');
  }, [showChart]);
  

  // date range (persisted)
  const [startDate, setStartDate] = useState(() => localStorage.getItem('aiStart') || '');
  const [endDate, setEndDate]     = useState(() => localStorage.getItem('aiEnd')   || '');
  useEffect(() => { localStorage.setItem('aiStart', startDate); }, [startDate]);
  useEffect(() => { localStorage.setItem('aiEnd',   endDate);   }, [endDate]);

  const userId = localStorage.getItem('userId');
  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal ? 'http://localhost:5000' : 'https://www.trendscout.club';

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    Promise.all([
      axios.get(`${baseUrl}/api/ai-completed-docs`),
      // axios.get(`${baseUrl}/api/ai-completed-stats`) // if you still use it
    ])
      .then(([docsRes /*, statsRes*/]) => {
        if (cancelled) return;
        setDocs(docsRes.data || []);
        console.log(docsRes.data)
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Fetch failed:', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [baseUrl]);


  // -------- helpers --------
  const lower = (v) => (v ?? '').toString().toLowerCase();


  const extractTrendCore = (trendString) => {
    const trend = lower(trendString);
    if (trend.includes('bullish')) return 'Bullish';
    if (trend.includes('bearish')) return 'Bearish';
    if (trend.includes('neutral')) return 'Neutral';
    return 'Other';
  };


  const inDateRange = (ts) => {
    if (!startDate && !endDate) return true;
    const t = new Date(ts).getTime();
    const start = startDate ? new Date(startDate + 'T00:00:00').getTime() : -Infinity;
    const end   = endDate   ? new Date(endDate   + 'T23:59:59.999').getTime() :  Infinity;
    return t >= start && t <= end;
  };

  const filteredDocs = useMemo(() => {
    const byOutcome = docs.filter((doc) => {
      if (filter === 'tp')   return doc.outcome?.targetHit === 'tp';
      if (filter === 'sl')   return doc.outcome?.targetHit === 'sl';
      if (filter === 'none') return doc.outcome?.targetHit === 'none';
      return true;
    });
    return byOutcome.filter((d) => inDateRange(d.timestamp));
  }, [docs, filter, startDate, endDate]);

  const finalDocs = useMemo(
  () => (advancedPredicate ? filteredDocs.filter(advancedPredicate) : filteredDocs),
  [filteredDocs, advancedPredicate]
);

  // headline stats derived from filtered set
  const filteredStats = useMemo(() => {
    const total = filteredDocs.length;
    const tp = filteredDocs.filter((d) => d.outcome?.targetHit === 'tp').length;
    const sl = filteredDocs.filter((d) => d.outcome?.targetHit === 'sl').length;
    const neither = total - tp - sl;

    const sumPL = filteredDocs.reduce(
      (s, d) => s + (typeof d?.outcome?.profitLoss === 'number' ? d.outcome.profitLoss : 0),
      0
    );
    const sumTime = filteredDocs.reduce(
      (s, d) => s + (Number(d?.outcome?.daysToOutcome) || 0),
      0
    );

    const avgProfitLoss = total ? sumPL / total : 0;
    const avgDaysToOutcome = total ? sumTime / total : 0;

    const accuracyRatings = filteredDocs.reduce((acc, d) => {
      const r = d?.outcome?.aiAccuracyRating || 'Unknown';
      acc[r] = (acc[r] || 0) + 1;
      return acc;
    }, {});

    const correct = filteredDocs.filter((d) =>
      d.outcome?.aiAccuracyRating?.toLowerCase().includes('high')
    ).length;
    const incorrect = total - correct;

    return { total, tp, sl, neither, avgProfitLoss, avgDaysToOutcome, accuracyRatings, correct, incorrect };
  }, [filteredDocs]);

  // table sort over filtered set
  const sortedDocs = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return filteredDocs;

    const getValue = (doc) => {
      switch (key) {
        case 'timestamp':     return new Date(doc.timestamp);
        case 'token':         return doc.token;
        case 'source':        return doc.analysis?.source;
        case 'marketCap':     return doc.analysis?.marketCap;
        case 'recommendation':return doc.analysis?.recommendation;
        case 'trend':         return extractTrendCore(doc.analysis?.trendStrength);
        case 'priceChange24h':return parseFloat(doc.analysis?.priceChange24h);
        case 'confidence':    return doc.analysis?.confidenceLevel;
        case 'score':         return doc.analysis?.score;
        case 'scoreMeaning':  return doc.analysis?.scoreMeaning;
        case 'targetHit':     return doc.outcome?.targetHit;
        case 'daysToOutcome': return doc.outcome?.daysToOutcome;
        case 'profitLoss':    return doc.outcome?.profitLoss;
        case 'accuracy':      return doc.outcome?.aiAccuracyRating;
        default:              return null;
      }
    };

    const arr = [...filteredDocs];
    arr.sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (typeof av === 'string' && typeof bv === 'string') {
        return direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      return direction === 'asc' ? (av - bv) : (bv - av);
    });
    return arr;
  }, [filteredDocs, sortConfig]);

  const toggleSort = (key) => {
    setSortConfig((prev) => (prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }
    ));
  };

  const formatNumberShort = (n) => {
    if (n == null || isNaN(n)) return 'N/A';
    if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + ' B';
    if (n >= 1_000_000)     return (n / 1_000_000).toFixed(1) + ' M';
    if (n >= 1_000)         return (n / 1_000).toFixed(1) + ' K';
    return n.toString();
  };

  const handleCalculate = () => {
    if (!investment || !leverage) return;
    const capital = parseFloat(investment);
    const lev = parseFloat(leverage);
    let totalReturn = 0;

    filteredDocs.forEach((doc) => {
      const pl = doc.outcome?.profitLoss;
      const accuracy = doc.outcome?.aiAccuracyRating;
      if (typeof pl === 'number') {
        const isHighAccuracy = accuracy?.toLowerCase() !== 'low';
        const effectivePL = isHighAccuracy ? pl : -Math.abs(pl);
        totalReturn += capital * lev * (effectivePL / 100);
      }
    });

    setEstimatedReturn(totalReturn.toFixed(2));
  };

  // render
if (isLoading) {
  return (
    <div className="loading-modal" role="alert" aria-busy="true" aria-live="polite">
      <div className="loading-content">
        <div className="loader" />
        <p>Loading AI data...</p>
      </div>
    </div>
  );
}

if (error) {
  return (
    <div className="error-box">
      <strong>Could not load data.</strong>
      <div style={{ marginTop: 6 }}>{String(error?.message || error)}</div>
    </div>
  );
}


  return (
    <div className="outcome-table-wrapper-dashboard">
      <div className="outcome-table">
        <h2>📊 AI Completed Trade Performance Dashboard</h2>

        <div className="expanded-content">
          <div className="expanded-section">
            <h4>📈 Performance Summary (filtered)</h4>
            <p><strong>Total Trades:</strong> {filteredStats.total}</p>
            <p><strong>TP Hits:</strong> {filteredStats.tp}</p>
            <p><strong>SL Hits:</strong> {filteredStats.sl}</p>
            <p><strong>Neither Hit:</strong> {filteredStats.neither}</p>
            <p><strong>Correct:</strong> {filteredStats.correct}</p>
            <p><strong>Incorrect:</strong> {filteredStats.incorrect}</p>
          </div>

          <div className="expanded-section">
            <h4>💡 Averages (filtered)</h4>
            <p><strong>Profit/Loss:</strong> {filteredStats.avgProfitLoss.toFixed(2)}%</p>
            <p><strong>Time to Outcome:</strong> {filteredStats.avgDaysToOutcome.toFixed(2)} Hrs</p>
            {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
              <div className="aidashbuttoncontainer">
                <button
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${baseUrl}/api/exportExcel`, {
                        responseType: 'blob',
                        headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
                      });
                      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url; a.download = 'aiCompleted.xlsx';
                      document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Excel download failed:', err);
                      alert('Could not download Excel file');
                    }
                  }}
                >
                  ⬇️ Download Excel
                </button>
              </div>
            )}
            <div className="aidashbuttoncontainer">
              <button onClick={() => setShowChart((s) => !s)}>
                {showChart ? 'Hide Chart' : 'Show Chart'}
              </button>
            </div>
          </div>

          <div className="expanded-section">
            <h4>🎯 Accuracy Ratings (filtered)</h4>
            {Object.entries(filteredStats.accuracyRatings).map(([rating, count]) => (
              <p key={rating}><strong>{rating}:</strong> {count}</p>
            ))}
            {userId === 'pziPDtOgJiX06oGuJxNwxE' && (
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get(`${baseUrl}/api/exportJson`, {
                      responseType: 'blob',
                      headers: { 'Accept': 'application/json' },
                    });
                    const blob = new Blob([res.data], { type: 'application/json' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'aiCompleted.json';
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('JSON download failed:', err);
                    alert('Could not download JSON');
                  }
                }}
                style={{ margin: '10px 0' }}
              >
                ⬇️ Export JSON
              </button>
            )}
          </div>

          <div className="expanded-section">
            <h4>📅 Date Range</h4>
            <label>From</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <label>To</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            <button onClick={() => { setStartDate(''); setEndDate(''); }}>Clear</button>
          </div>

          <div className="expanded-section">
            <h4>📈 Profit Calculator (filtered)</h4>
            <label>Investment ($):</label>
            <input type="number" value={investment} onChange={(e) => setInvestment(e.target.value)} />
            <label>Leverage (x):</label>
            <input type="number" value={leverage} onChange={(e) => setLeverage(e.target.value)} />
            <button onClick={handleCalculate} style={{ marginTop: '8px' }}>Calculate</button>
            {estimatedReturn !== null && (
              <p style={{ marginTop: '10px' }}>
                <strong>Estimated Return:</strong> ${estimatedReturn}
              </p>
            )}
          </div>
        </div>

      {showChart && (
        <div style={{ margin: '16px 0' }}>
          <TradesDayHourChart
            docs={finalDocs}
            height={560}
            groupByDate={groupByDate}
            onChangeGroupMode={setGroupByDate}  // <- NEW
          />
        </div>
      )}


        <details className="expanded-section" open>
          <summary>🔍 Perfect Criteria Finder</summary>
          <CriteriaFinder docs={filteredDocs.filter(d => (d.token || '').toUpperCase() !== 'BTC/USDT')} />
        </details>
        <div style={{ marginTop: 24 }}>
            <CriteriaExplorer
              docs={filteredDocs /* your already date/outcome-filtered array */}
              onApply={(pred) => setAdvancedPredicate(() => pred)} // optional
            />
        </div>
        <div className="filter-bar" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All</option>
            <option value="tp">TP Hits</option>
            <option value="sl">SL Hits</option>
            <option value="none">Neither</option>
          </select>
        </div>

        <div className="table-scroll-container">
          <table>
            <thead>
              <tr>
                <th onClick={() => toggleSort('timestamp')}>Date</th>
                <th onClick={() => toggleSort('token')}>Token</th>
                <th onClick={() => toggleSort('source')}>Source</th>
                <th onClick={() => toggleSort('marketCap')}>MC</th>
                <th onClick={() => toggleSort('recommendation')}>Rec.</th>
                <th onClick={() => toggleSort('trend')}>Trend</th>
                <th onClick={() => toggleSort('priceChange24h')}>24h</th>
                <th onClick={() => toggleSort('confidence')}>Conf.</th>
                <th onClick={() => toggleSort('score')}>Score</th>
                <th onClick={() => toggleSort('scoreMeaning')}>Meaning</th>
                <th onClick={() => toggleSort('targetHit')}>Hit</th>
                <th onClick={() => toggleSort('daysToOutcome')}>Time</th>
                <th onClick={() => toggleSort('profitLoss')}>P/L</th>
                <th onClick={() => toggleSort('accuracy')}>Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {sortedDocs.map((doc, idx) => (
                <tr key={idx}>
                  <td>{new Date(doc.timestamp).toLocaleString()}</td>
                  <td>{doc.token}</td>
                  <td>{doc.analysis?.source}</td>
                  <td>${formatNumberShort(doc.analysis?.marketCap)}</td>
                  <td>{doc.analysis?.recommendation || '-'}</td>
                  <td>{extractTrendCore(doc.analysis?.trendStrength)}</td>
                  <td>
                    {typeof doc.analysis?.priceChange24h === 'number'
                      ? `${doc.analysis.priceChange24h.toFixed(2)}%`
                      : !isNaN(parseFloat(doc.analysis?.priceChange24h))
                        ? `${parseFloat(doc.analysis.priceChange24h).toFixed(2)}%`
                        : '-'}
                  </td>
                  <td>{doc.analysis?.confidenceLevel || '-'}</td>
                  <td>{doc.analysis?.score || '-'}</td>
                  <td>{doc.analysis?.scoreMeaning || '-'}</td>
                  <td>{doc.outcome?.targetHit || '-'}</td>
                  <td>{doc.outcome?.daysToOutcome || '-'} Hrs</td>
                  <td>{typeof doc.outcome?.profitLoss === 'number' ? `${doc.outcome.profitLoss.toFixed(2)}%` : '-'}</td>
                  <td>{doc.outcome?.aiAccuracyRating || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
}
